import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { testimonials } from '@/mocks/testimonials';
import FAQSection from '@/pages/home/components/FAQSection';
import { aboutFaqs } from '@/mocks/faq';

const values = [
  { icon: 'ri-heart-line', title: 'People First', desc: 'Every decision we make is guided by the well-being and success of our participants. We see the person behind the challenge, not just the challenge.' },
  { icon: 'ri-focus-3-line', title: 'Outcome Obsessed', desc: 'We measure our success by employment placements, not program completions. Everything is built to produce real, lasting results in people\'s lives.' },
  { icon: 'ri-community-line', title: 'Community Rooted', desc: 'OST-C was built by the community, for the community. Our mission is inseparable from the people and neighborhoods we serve in DFW.' },
  { icon: 'ri-shield-check-line', title: 'Inclusive & Accessible', desc: 'We serve everyone — regardless of background, circumstance, or past challenges. Barriers are meant to be removed, not reinforced.' },
];

const AboutPage = () => {
  const heroRef = useScrollReveal();
  const storyRef = useScrollReveal();
  const valuesRef = useScrollReveal();
  const metricsRef = useScrollReveal();

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-center bg-charcoal overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3f735510d683e4404297f2552b4d2b76.jpg"
            alt="OST-C team - Occupational Skills Training Center Arlington TX"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/80 to-charcoal/50" />
        </div>
        <div ref={heroRef} className="reveal relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">About OST-C</span>
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 max-w-3xl">
            A Higher Standard in Occupational Skills Training
          </h1>
          <p className="font-inter text-base text-white/65 max-w-2xl leading-relaxed">
            Since 2012, OST-C has been transforming lives across Arlington and the DFW region through vocational education, job readiness programs, and community-centered workforce development.
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-charcoal border-y border-white/10 py-5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-between gap-y-4">
            {[
              { icon: 'ri-user-follow-line', label: '85%+ Job Placement Rate' },
              { icon: 'ri-calendar-check-line', label: 'Est. 2012 — 10+ Years' },
              { icon: 'ri-community-line', label: '500+ Graduates' },
              { icon: 'ri-seedling-line', label: 'Life Skills Integrated' },
              { icon: 'ri-links-line', label: 'Employer Partnerships' },
              { icon: 'ri-star-line', label: '4.9 Participant Rating' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3">
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className={`${stat.icon} text-gold text-base`} />
                </div>
                <span className="font-inter text-[13px] font-medium text-white/70 whitespace-nowrap">{stat.label}</span>
                {i < 5 && <div className="hidden lg:block w-[1px] h-5 bg-white/15 ml-3" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="bg-white py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div ref={storyRef} className="reveal">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-[2px] bg-gold" />
                <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Our Story</span>
              </div>
              <h2 className="font-playfair text-4xl font-bold text-charcoal mb-6 leading-tight">
                Built to Change Lives Through Workforce Education
              </h2>
              <p className="font-inter text-base text-charcoal/65 leading-relaxed mb-5">
                The Occupational Skills Training Center was founded in 2012 with a clear and urgent mission: to close the gap between untapped human potential and real economic opportunity. We saw individuals in our community with the drive to succeed but without access to the training, structure, and support to get there.
              </p>
              <p className="font-inter text-base text-charcoal/65 leading-relaxed mb-5">
                We built OST-C to change that. Our programs were designed from the ground up to produce employment — not just certificates. We combined vocational training with life skills development, job readiness coaching, and direct employer connections to create a complete pathway from where you are to where you want to be.
              </p>
              <p className="font-inter text-base text-charcoal/65 leading-relaxed">
                Over a decade later, we have placed over 500 graduates into meaningful employment, built deep employer partnerships across the DFW region, and continued to expand our programs to serve more communities, more circumstances, and more futures.
              </p>
            </div>
            <div className="relative">
              <div className="w-full h-[480px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=inspiring%20community%20vocational%20training%20center%20classroom%20diverse%20motivated%20adults%20learning%20job%20skills%20empowerment%20education%20workforce%20development%20program%20hopeful%20bright%20professional%20training%20environment&width=720&height=800&seq=about-story-06&orientation=portrait"
                  alt="OST-C vocational training programs Arlington TX"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 bg-charcoal/90 backdrop-blur-sm rounded-lg p-5 border border-gold/20">
                <p className="font-inter text-xs text-gold uppercase tracking-widest font-semibold mb-1">Arlington, TX — Since 2012</p>
                <p className="font-playfair text-lg font-bold text-white">Occupational Skills Training Center</p>
                <p className="font-inter text-sm text-white/55 mt-1">Transforming lives through vocational excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="w-16 h-1 bg-gold mb-6" />
            <h2 className="font-playfair text-4xl font-bold text-white leading-tight">
              Who Is OST-C?
            </h2>
          </div>
          <div>
            <p className="font-inter text-base text-white/70 leading-relaxed mb-5">
              <strong className="text-white">OST-C (Occupational Skills Training Center)</strong> is a community-centered provider of vocational training, job readiness programming, and life skills development based in Arlington, TX. Since 2012, the organization has focused on a single mission: producing real employment outcomes for individuals who need structured, accessible workforce training.
            </p>
            <p className="font-inter text-sm text-white/55 leading-relaxed mb-5">
              OST-C serves first-time job seekers, career changers, returning citizens, and individuals facing employment barriers. Their approach combines hands-on vocational instruction with professional readiness coaching, life skills integration, and direct employer placement support — creating a complete, individualized path from enrollment to sustained employment.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              {['Vocational Training', 'Job Readiness', 'Life Skills', 'Reintegration', 'Workforce Development'].map((tag) => (
                <span key={tag} className="font-inter text-xs font-medium text-gold border border-gold/30 px-3 py-1.5 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold" />
              <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Our Values</span>
              <div className="w-8 h-[2px] bg-gold" />
            </div>
            <h2 className="font-playfair text-4xl font-bold text-charcoal">The Philosophy Behind Everything We Do</h2>
          </div>
          <div ref={valuesRef} className="reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-lg p-7 border border-cream-100 hover:border-gold/30 transition-colors duration-200">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-lg mb-5">
                  <i className={`${v.icon} text-gold text-xl`} />
                </div>
                <h3 className="font-playfair text-xl font-bold text-charcoal mb-3">{v.title}</h3>
                <p className="font-inter text-sm text-charcoal/60 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold" />
              <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Our Impact</span>
              <div className="w-8 h-[2px] bg-gold" />
            </div>
            <h2 className="font-playfair text-4xl font-bold text-charcoal">Numbers That Reflect Real Lives Changed</h2>
          </div>
          <div ref={metricsRef} className="reveal grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: '85%+', label: 'Job Placement Rate', desc: 'Graduate employment within 90 days' },
              { value: '500+', label: 'Successful Graduates', desc: 'Placed into sustainable employment' },
              { value: '10+', label: 'Years Serving DFW', desc: 'Community-rooted since 2012' },
              { value: '6', label: 'Training Programs', desc: 'Covering vocational through workforce' },
            ].map((m) => (
              <div key={m.label} className="bg-cream rounded-lg p-8 text-center border border-cream-100">
                <p className="font-playfair text-5xl font-bold text-gold mb-2">{m.value}</p>
                <p className="font-inter font-semibold text-charcoal text-sm mb-1">{m.label}</p>
                <p className="font-inter text-xs text-charcoal/50">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Community Impact</span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl font-bold text-white mb-5">Rooted in Arlington. Serving All of DFW.</h2>
          <p className="font-inter text-base text-white/60 max-w-2xl mx-auto mb-10">
            OST-C provides occupational training and job placement support to individuals across Arlington and the greater Dallas–Fort Worth metroplex — including Fort Worth, Grand Prairie, Mansfield, Euless, Irving, and surrounding communities.
          </p>
          <Link to="/location" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer">
            View Location &amp; Service Areas
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-cream py-20 px-6">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Graduate Stories</span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl font-bold text-charcoal">Voices of Transformation</h2>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.slice(2, 4).map((t) => (
            <div key={t.id} className="bg-white rounded-lg p-8 border border-cream-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gold" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (<i key={i} className="ri-star-fill text-gold text-xs" />))}
              </div>
              <blockquote className="font-inter text-sm text-charcoal/75 leading-relaxed italic mb-5">&ldquo;{t.quote}&rdquo;</blockquote>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-inter font-semibold text-charcoal text-sm">{t.name}</p>
                  <p className="font-inter text-xs text-charcoal/50">{t.detail}</p>
                </div>
                <span className="font-inter text-xs font-bold text-gold bg-gold/10 border border-gold/20 px-3 py-1 rounded-full">{t.outcome}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={aboutFaqs} title="About OST-C" subtitle="Common Questions" />

      {/* Final CTA */}
      <section className="bg-charcoal py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Get Started</span>
            <div className="w-8 h-[2px] bg-gold" />
          </div>
          <h2 className="font-playfair text-4xl font-bold text-white mb-5">
            Join a Program That Changes Lives
          </h2>
          <p className="font-inter text-base text-white/60 mb-8 max-w-xl mx-auto">
            Whether you&apos;re starting fresh or rebuilding, OST-C has a program for your goals. Enrollment is open — let&apos;s build your future together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer">
              Book a Consultation <i className="ri-arrow-right-line" />
            </Link>
            <a href="tel:4698535038" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-inter font-semibold text-sm px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line" /> Call 469-853-5038
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default AboutPage;
